import { useState, useEffect, useCallback } from 'react'
import { productApi, Product, Category, ProductListResponse } from '../services/api'

interface UseProductsReturn {
  products: Product[]
  categories: Category[]
  loading: boolean
  error: string | null
  totalPages: number
  currentPage: number
  hasNext: boolean
  hasPrev: boolean
  loadProducts: (params?: {
    page?: number
    limit?: number
    category_id?: string
    sort_by?: string
    sort_order?: string
  }) => Promise<void>
  searchProducts: (params: {
    query?: string
    category_id?: string
    category_ids?: string
    min_price?: number
    max_price?: number
    tags?: string
    attrs?: Record<string, any>
    page?: number
    limit?: number
    country_code?: string
    city_name?: string
  }) => Promise<void>
  loadCategories: () => Promise<void>
  nextPage: () => Promise<void>
  prevPage: () => Promise<void>
}

export function useProducts(): UseProductsReturn {
  const [products, setProducts] = useState<Product[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [totalPages, setTotalPages] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasNext, setHasNext] = useState(false)
  const [hasPrev, setHasPrev] = useState(false)
  const [lastParams, setLastParams] = useState<any>({})

  // Load products
  const loadProducts = useCallback(async (params: {
    page?: number
    limit?: number
    category_id?: string
    sort_by?: string
    sort_order?: string
  } = {}) => {
    try {
      setLoading(true)
      setError(null)

      const response: ProductListResponse = await productApi.getProducts(params)

      // Ensure products is always an array
      setProducts(response.products || [])
      setTotalPages(response.total_pages)
      setCurrentPage(response.page)
      setHasNext(response.has_next)
      setHasPrev(response.has_prev)
      setLastParams(params)

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load products'
      setError(errorMessage)
      console.error('Failed to load products:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  // Search products
  const searchProducts = useCallback(async (params: {
    query?: string
    category_id?: string
    category_ids?: string
    min_price?: number
    max_price?: number
    tags?: string
    attrs?: Record<string, any>
    page?: number
    limit?: number
    country_code?: string
    city_name?: string
  }) => {
    try {
      setLoading(true)
      setError(null)

      const response: ProductListResponse = await productApi.searchProducts(params)

      // Ensure products is always an array
      setProducts(response.products || [])
      setTotalPages(response.total_pages)
      setCurrentPage(response.page)
      setHasNext(response.has_next)
      setHasPrev(response.has_prev)
      setLastParams(params)

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to search products'
      setError(errorMessage)
      console.error('Failed to search products:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  // Load categories
  const loadCategories = useCallback(async () => {
    try {
      const response = await productApi.getCategories()
      setCategories(response.categories)
    } catch (err) {
      console.error('Failed to load categories:', err)
    }
  }, [])

  // Go to next page
  const nextPage = useCallback(async () => {
    if (hasNext) {
      await loadProducts({
        ...lastParams,
        page: currentPage + 1,
      })
    }
  }, [hasNext, currentPage, lastParams, loadProducts])

  // Go to previous page
  const prevPage = useCallback(async () => {
    if (hasPrev) {
      await loadProducts({
        ...lastParams,
        page: currentPage - 1,
      })
    }
  }, [hasPrev, currentPage, lastParams, loadProducts])

  // Load initial data on mount
  useEffect(() => {
    loadProducts()
    loadCategories()
  }, [loadProducts, loadCategories])

  return {
    products,
    categories,
    loading,
    error,
    totalPages,
    currentPage,
    hasNext,
    hasPrev,
    loadProducts,
    searchProducts,
    loadCategories,
    nextPage,
    prevPage,
  }
}